import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Event, EventService } from "src/openapi"
import { Box, Modal, Typography } from "src/UILibrary"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import { useLoginContext } from "src/hooks/useLoginContext"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { APPLICATION_STATUS, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { ShowProgress } from "src/states/showProgress"
import { validateField } from "src/modules/validation"
import * as yup from "yup"
import InputTextField from "src/components/atom/InputTextField"

interface Props {
  item: Event
  joinType: string
  open: boolean
  handleOpen: any
  refreshEvents?: any
}

export const EventBehalfJoinModal = ({
  item,
  joinType,
  open,
  handleOpen,
  refreshEvents,
}: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "event" })

  const { accessToken, checkLoginStatus } = useLoginContext()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [cnt, setCnt] = useState<string>("")

  const [cntValidationMessage, setCntValidationMessage] = useState<string>("")

  const getApplicationCount = () => {
    if (!item.eventApplications) {
      return 0
    }

    let count = 0
    for (const application of item.eventApplications) {
      if (application.application?.status === APPLICATION_STATUS.RESERVED) {
        count++
      }
    }
    return count
  }

  const validate = async () => {
    try {
      await validateField(
        yup.object().shape({
          cnt: yup
            .string()
            .required(t("please_input_join_cnt"))
            .matches(/^\d+$/, t("please_input_numbers")),
        }),
        "cnt",
        cnt
      )
      setCntValidationMessage("")
      return true
    } catch (err: any) {
      setCntValidationMessage(err.message)
      return false
    }
  }

  const handleProxyJoin = async () => {
    if (!(await validate())) {
      return
    }

    if (getApplicationCount() + parseInt(cnt) > item.applicationMax!) {
      setShowToast({ show: true, text: t("join_behalf_limit"), type: SNACKBAR_TYPES.ERROR })
      return
    }

    setShowProgress(true)
    EventService.createProxyApplication(accessToken, item.id as number, {
      type: joinType,
      count: parseInt(cnt),
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          if (refreshEvents) {
            refreshEvents()
          }
          setShowToast({
            show: true,
            text: t("success_behalf_join"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          handleOpen(false)
        } else {
          setShowToast({ show: true, text: t("failed_behalf_join"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_behalf_join"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <Modal
      open={open}
      onClose={() => handleOpen(false)}
      containerProps={{ px: "30px", py: "20px" }}
    >
      <Box sx={{ p: 0 }}>
        <Box display="flex" height="50px" sx={{ borderBottom: "2px solid #D4D3D7" }}>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontWeight: "700",
              ml: "10px",
              my: "auto",
            }}
          >
            {t("behalf_join_title")}
          </Typography.InputFieldTitle>
          <CloseOutlinedIcon
            sx={{
              my: "auto",
              ml: "auto",
              mr: "20px",
              width: "30px",
              height: "30px",
              ":hover": { opacity: 0.8, cursor: "pointer" },
            }}
            onClick={() => handleOpen(false)}
          />
        </Box>
        <Box display="flex" sx={{ borderBottom: "1px dashed #D4D3D7" }}>
          <Box display="flex" sx={{ m: "40px" }}>
            <Typography.DetailText
              sx={{
                fontFamily: "Noto Sans JP !important",
                fontWeight: "700",
                color: "#1F6757",
                minWidth: "60px",
              }}
            >
              {t("event_title")}
            </Typography.DetailText>
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", ml: "20px" }}
            >
              {item.title}
            </Typography.DetailText>
          </Box>
        </Box>

        <Typography.SubTitle
          sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
        >
          {t("behalf_join_description")}
        </Typography.SubTitle>

        <InputTextField
          sx={{ mt: "20px" }}
          name={t("behalf_cnt")}
          text={cnt}
          handleText={setCnt}
          validationMessage={cntValidationMessage}
          placeholder={t("please_input")}
          inputStyle={{ width: "200px" }}
        />

        <OutlinedButton
          caption={t("behalf_join")}
          sx={{ mt: "20px", borderColor: "primary.main" }}
          fontColor="primary.main"
          onClick={() => handleProxyJoin()}
        />
      </Box>
    </Modal>
  )
}
